import { Button, DataForm, Field, InfoCard, Input, P, Stack } from "@introist/react-foundation/v2";
import { Block } from "components/atoms";
import { MessageEditor as MessageEditorV4 } from "components/editors/v4/MessageEditor/MessageEditor";
import { StepEditorAccordion } from "modules/workflows/routes/WorkflowEditor/StepEditorAccordion";
import { prefixForm } from "utils/FormUtils";
import { api } from "services/rpc/RpcProvider";
import { useNavigate } from "react-router";

interface StepRemindersBlockProps {
  form: DataForm<any>;
  stepDataPath: string;
  workflowId: string;
}

type StepReminder = {
  maxCount: number;
  intervalDays: number;
  message: string;
};

const defaultReminder = (): StepReminder => ({
  maxCount: 3,
  intervalDays: 2,
  message: ""
});

export const StepRemindersBlock = ({ form, stepDataPath, workflowId }: StepRemindersBlockProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);
  const navigate = useNavigate();

  // Get the sender ID from the form
  const senderId = prefixedForm.get("emailSenderId");

  // Fetch connected accounts to check threading scopes
  const { data: connectedAccounts } = api.connectedAccounts.list.useQuery({});
  const sender = connectedAccounts?.find(account => account.id === senderId);

  const reminders: StepReminder[] | undefined = prefixedForm.get("reminders");
  const reminder: StepReminder | undefined = reminders?.[0];

  const onAdd = () => {
    prefixedForm.set("reminders")([defaultReminder()]);
  };

  const onRemove = () => {
    prefixedForm.set("reminders")(undefined);
  };

  const onUpdate = (updated: Partial<StepReminder>) => {
    prefixedForm.set("reminders")([{ ...reminder!, ...updated }]);
  };

  // Check if sender needs reconnection for step reminders
  const needsReconnection =
    reminder &&
    sender &&
    sender.emailProvider === "outlook" &&
    sender.hasThreadingScopes === false;

  const handleReconnect = () => {
    navigate(`/settings/accounts?reconnect=${sender?.emailProvider}`);
  };

  return (
    <Block compact blended>
      <StepEditorAccordion title="Reminders" defaultOpen={!!reminder}>
        {!reminder ? (
          <Button variant="outlined" onClick={onAdd}>
            Add reminder
          </Button>
        ) : (
          <Stack vertical gap="xLarge">
            {needsReconnection && (
              <InfoCard
                title="Reconnect your Outlook account to enable step reminders."
                colorVariant="warning"
              >
                <Button variant="outlined" size="small" onClick={handleReconnect}>
                  Reconnect Account
                </Button>
              </InfoCard>
            )}
            <Stack gap="small" alignItems="center">
              <P>Send up to</P>
              <Input
                type="number"
                min={1}
                value={reminder.maxCount}
                onChange={val => onUpdate({ maxCount: parseInt(val) || 1 })}
                size="small"
                style={{ width: "60px" }}
              />
              <P>times, every</P>
              <Input
                type="number"
                min={1}
                value={reminder.intervalDays}
                onChange={val => onUpdate({ intervalDays: parseInt(val) || 1 })}
                size="small"
                style={{ width: "60px" }}
              />
              <P>days</P>
            </Stack>
            <Field title="Message">
              <MessageEditorV4
                message={reminder.message ?? ""}
                onChange={message => onUpdate({ message })}
                workflowId={workflowId}
              />
            </Field>
            <Button variant="outlined" onClick={onRemove}>
              Remove reminder
            </Button>
          </Stack>
        )}
      </StepEditorAccordion>
    </Block>
  );
};
